// components/Sender/SenderLogTab.js
import React, { useEffect, useMemo, useRef, useState } from "react";

const LEVEL_COLORS = {
  info: "#93c5fd",
  warn: "#fbbf24",
  error: "#f87171",
  debug: "#64748b",
};

const KIND_CFG = {
  chrome:  { icon: "🌐", label: "Chrome",  bg: "#dbeafe", fg: "#1d4ed8" },
  android: { icon: "📱", label: "Android", bg: "#cffafe", fg: "#0891b2" },
};

function _runtimeMeta(rid, runtimes) {
  const rt = (runtimes || []).find((r) => r.id === rid);
  const kind = rt?.kind || "unknown";
  return KIND_CFG[kind] || { icon: "❔", label: rid, bg: "#f3f4f6",
                             fg: "#6b7280" };
}

export function SenderLogTab({
  logs, lastRun, runtimes, onClear, clearing, onMessageClick,
}) {
  const [activeRuntime, setActiveRuntime] = useState("__all__");
  const boxRef = useRef(null);
  const tabsRef = useRef(null);

  // Список runtime_id'ов из логов + счётчики
  const { runtimeIds, counts } = useMemo(() => {
    const ids = new Set();
    const c = { __all__: logs.length };
    logs.forEach((l) => {
      if (l.runtime_id) {
        ids.add(l.runtime_id);
        c[l.runtime_id] = (c[l.runtime_id] || 0) + 1;
      }
    });
    return { runtimeIds: Array.from(ids).sort(), counts: c };
  }, [logs]);

  // Если активный runtime пропал — переключаемся на "Все"
  useEffect(() => {
    if (activeRuntime !== "__all__" && !runtimeIds.includes(activeRuntime)) {
      setActiveRuntime("__all__");
    }
  }, [runtimeIds, activeRuntime]);

  // Фильтр
  const filtered = useMemo(() => {
    if (activeRuntime === "__all__") return logs;
    return logs.filter((l) => l.runtime_id === activeRuntime);
  }, [logs, activeRuntime]);

  // Автоскролл только если пользователь внизу
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [filtered]);

  // Скролл к активной вкладке
  useEffect(() => {
    const tabs = tabsRef.current;
    if (!tabs) return;
    const active = tabs.querySelector("[data-active='true']");
    if (active) {
      const rect = active.getBoundingClientRect();
      const parentRect = tabs.getBoundingClientRect();
      if (rect.right > parentRect.right || rect.left < parentRect.left) {
        active.scrollIntoView({ behavior: "smooth", inline: "center" });
      }
    }
  }, [activeRuntime, runtimeIds.length]);

  const runLabel = lastRun
    ? `${lastRun.template_name || lastRun.id}` +
      (lastRun.status === "running" ? " · ▶ live"
        : lastRun.status === "stopping" ? " · ⏹ остановка"
        : lastRun.status === "finished" ? " · ✅"
        : lastRun.status === "stopped" ? " · ⏹"
        : lastRun.status === "error" ? " · ✖"
        : "")
    : null;

  return (
    <div className="section-card" style={{ display: "flex",
                                            flexDirection: "column" }}>
      {/* Шапка */}
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: 10,
                    gap: 8, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: 0 }}>
            📜 Журнал
            {runLabel && (
              <span style={{ fontSize: 13, fontWeight: 400,
                              color: "#6b7280", marginLeft: 10 }}>
                · последний run: <b>{runLabel}</b>
              </span>
            )}
          </h3>
          {lastRun?.id && (
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4,
                          fontFamily: "Consolas, monospace" }}>
              id: {lastRun.id}
              {" · "}
              {logs.length} записей
              {runtimeIds.length > 0 && ` · ${runtimeIds.length} runtime'ов`}
            </div>
          )}
        </div>
        <button className="btn-cancel" onClick={onClear}
                disabled={clearing || !lastRun}>
          {clearing ? "⏳ Очистка…" : "🧹 Очистить"}
        </button>
      </div>

      {/* Вкладки runtime'ов (браузер-стайл) */}
      {lastRun && (
        <div ref={tabsRef} style={tabsContainer}>
          <TabBtn
            label="Все"
            icon="📋"
            count={counts.__all__ || 0}
            active={activeRuntime === "__all__"}
            onClick={() => setActiveRuntime("__all__")}
            bg="#f3f4f6"
            fg="#374151"
          />
          {runtimeIds.map((rid) => {
            const meta = _runtimeMeta(rid, runtimes);
            return (
              <TabBtn
                key={rid}
                label={rid}
                icon={meta.icon}
                count={counts[rid] || 0}
                active={activeRuntime === rid}
                onClick={() => setActiveRuntime(rid)}
                bg={meta.bg}
                fg={meta.fg}
              />
            );
          })}
        </div>
      )}

      {/* Лог-бокс */}
      <div ref={boxRef}
           style={{
             background: "#0b1220", color: "#cbd5e1",
             borderRadius: 10, padding: 12, height: 520,
             overflowY: "auto", marginTop: 10,
             fontFamily: "Consolas, monospace", fontSize: 13,
             lineHeight: 1.5,
           }}>
        {!lastRun ? (
          <div style={{ color: "#64748b" }}>
            Пока нет ни одного запуска. Создайте рассылку в 📦 Шаблоны.
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ color: "#64748b" }}>
            {logs.length === 0
              ? "Ждём первые сообщения…"
              : activeRuntime === "__all__"
                ? "Пока нет записей"
                : `Для runtime «${activeRuntime}» пока нет записей`}
          </div>
        ) : (
          filtered.map((l, i) => {
            if (l.kind === "message" && l.payload) {
              const info = l.payload;
              const icon = info.test ? "🧪" : "📤";
              const chShort = info.channel === "EMAIL" ? "EMAIL" : "CF";
              return (
                <div key={l.id || i}
                     style={{
                       color: "#cbd5e1", marginBottom: 2,
                       fontFamily: "Consolas, monospace", fontSize: 13,
                     }}>
                  <span style={{ color: "#475569", marginRight: 6 }}>
                    {fmtTime(l.ts)}
                  </span>
                  {icon}{" "}
                  {l.runtime_id && activeRuntime === "__all__" && (
                    <span style={inlineRuntimeBadge(
                      _runtimeMeta(l.runtime_id, runtimes))}>
                      {_runtimeMeta(l.runtime_id, runtimes).icon}
                    </span>
                  )}
                  {info.block_tag && (
                    <span style={{ color: "#64748b" }}>{info.block_tag} </span>
                  )}
                  <b style={{ color: "#a5b4fc" }}>#{info.intention_id}</b>{" "}
                  <span style={{ color: "#94a3b8" }}>[{chShort}]</span>{" "}
                  <span style={{ color: "#e2e8f0" }}>{info.worker}</span>{" "}
                  <span style={{ color: "#9ca3af" }}>→</span>{" "}
                  <span style={{ color: "#cbd5e1" }}>{info.address}</span>
                  {"  "}
                  <span onClick={() => onMessageClick?.(info)}
                        style={{ color: "#60a5fa", cursor: "pointer" }}>
                    детали
                  </span>
                </div>
              );
            }

            const color = LEVEL_COLORS[l.level] || "#cbd5e1";
            const icon =
              l.level === "error" ? "✖ "
              : l.level === "warn" ? "⚠ "
              : l.level === "info" ? "· "
              : "";
            return (
              <div key={l.id || i} style={{ color, marginBottom: 2 }}>
                <span style={{ color: "#475569", marginRight: 6 }}>
                  {fmtTime(l.ts)}
                </span>
                {l.runtime_id && activeRuntime === "__all__" && (
                  <span style={inlineRuntimeBadge(
                    _runtimeMeta(l.runtime_id, runtimes))}>
                    {_runtimeMeta(l.runtime_id, runtimes).icon}
                  </span>
                )}
                {icon}{l.msg}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function TabBtn({ label, icon, count, active, onClick, bg, fg }) {
  return (
    <button
      data-active={active ? "true" : "false"}
      onClick={onClick}
      style={{
        flex: "0 0 auto",
        display: "flex", alignItems: "center", gap: 6,
        padding: "6px 12px",
        borderRadius: 8,
        border: active ? `1.5px solid ${fg}` : "1px solid #e5e7eb",
        background: active ? bg : "#fff",
        color: active ? fg : "#6b7280",
        fontWeight: active ? 700 : 500,
        fontSize: 12,
        cursor: "pointer",
        whiteSpace: "nowrap",
        fontFamily: "inherit",
      }}>
      <span>{icon}</span>
      <span style={{ maxWidth: 180, overflow: "hidden",
                     textOverflow: "ellipsis" }}>
        {label}
      </span>
      <span style={{
        background: active ? fg : "#f3f4f6",
        color: active ? "#fff" : "#6b7280",
        fontSize: 10, fontWeight: 700,
        padding: "1px 6px", borderRadius: 10,
      }}>
        {count}
      </span>
    </button>
  );
}

function inlineRuntimeBadge(meta) {
  return {
    display: "inline-block",
    padding: "0 5px",
    marginRight: 4,
    borderRadius: 4,
    background: meta.bg,
    color: meta.fg,
    fontSize: 10,
    fontWeight: 700,
    verticalAlign: "baseline",
  };
}

function fmtTime(iso) {
  if (!iso) {
    const d = new Date();
    return d.toLocaleTimeString("ru-RU");
  }
  try { return new Date(iso).toLocaleTimeString("ru-RU"); }
  catch { return ""; }
}

const tabsContainer = {
  display: "flex",
  gap: 6,
  overflowX: "auto",
  overflowY: "hidden",
  paddingBottom: 6,
  scrollbarWidth: "thin",
  borderBottom: "1px solid #e5e7eb",
};